import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getMediaPushJob, clearMediaPushJob } from "../state/deviceInstallerSlice";
import type { ManifestModule, MediaPushMode } from "../utils/mediaPushOrchestrator";
import { fetchMediaManifest, startMediaPush } from "../utils/mediaPushOrchestrator";
import { scanDeviceMediaStatus, clearAllMediaFromDevice } from "../utils/adbMediaPusher";
import { scanAgentMediaStatus, clearAgentMedia } from "../utils/agentMediaPusher";
import MediaPushProgressModal from "./MediaPushProgressModal";
import { CheckIcon, InfoCircleIcon, Spinner, TrashIcon, WarningCircleIcon } from "./icons";
import ModalShell from "./ModalShell";
import { Tooltip } from "../../../common/components/Tooltip/Tooltip";

/** Format bytes to human-readable size */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Compute total size of a module's medias (use best preset size for videos when available) */
function getModuleSize(mod: ManifestModule, mode: MediaPushMode): number {
  return mod.medias.reduce((sum, m) => {
    let size = m.sizeInBytes;
    if (m.type === "video") {
      if (mode === "agent" && m.sizeInBytesPreset4) {
        size = m.sizeInBytesPreset4;
      } else if (m.sizeInBytesPreset2) {
        size = m.sizeInBytesPreset2;
      }
    }
    return sum + (size || 0);
  }, 0);
}

interface Props {
  projectGroupId: number;
  packageName: string;
  onClose: () => void;
  mode?: MediaPushMode;
  appId?: string;
}

/**
 * Media management modal:
 * 1. Module selection (fetch manifest → show checkboxes) with push & clear actions
 * 2. Push progress (delegates to MediaPushProgressModal)
 */
const MediaPushModal = ({ projectGroupId, packageName, onClose, mode = "adb", appId }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const mediaPushJob = useAppSelector(getMediaPushJob);

  const [modules, setModules] = useState<ManifestModule[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pushStarted, setPushStarted] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState<Map<number, boolean>>(new Map());
  const [clearing, setClearing] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchMediaManifest(projectGroupId)
      .then(async (manifest) => {
        if (cancelled) return;
        const mods = manifest.modules || [];
        setModules(mods);
        setSelectedIds(new Set(mods.map((m: ManifestModule) => m.id)));
        setLoading(false);

        const allMedias = mods.flatMap((m) => m.medias);
        if (allMedias.length > 0) {
          setScanning(true);
          try {
            const status =
              mode === "agent" && appId
                ? await scanAgentMediaStatus(appId, allMedias)
                : await scanDeviceMediaStatus(packageName, allMedias);
            if (!cancelled) setDeviceStatus(status);
          } catch (err) {
            console.warn("[MediaPush] media status scan failed:", err);
          } finally {
            if (!cancelled) setScanning(false);
          }
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = axios.isAxiosError<{ error?: { message?: string } }>(err)
          ? err.response?.data?.error?.message ?? err.message
          : err instanceof Error
          ? err.message
          : String(err);
        setError(message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectGroupId, packageName, mode, appId]);

  const toggleModule = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === modules.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(modules.map((m) => m.id)));
    }
  };

  const selectedModules = modules.filter((m) => selectedIds.has(m.id));
  const totalMediaCount = selectedModules.reduce((sum, m) => sum + m.medias.length, 0);
  const totalSize = selectedModules.reduce((sum, m) => sum + getModuleSize(m, mode), 0);

  const hasDeviceStatus = deviceStatus.size > 0;
  const getModulePresentCount = (mod: ManifestModule) =>
    mod.medias.filter((m) => deviceStatus.get(m.id) === true).length;
  const totalPresent = hasDeviceStatus
    ? selectedModules.reduce((sum, m) => sum + getModulePresentCount(m), 0)
    : 0;
  const totalToTransfer = totalMediaCount - totalPresent;

  const anyMediaOnDevice = hasDeviceStatus && Array.from(deviceStatus.values()).some((v) => v);

  const handleStart = () => {
    if (selectedModules.length === 0) return;
    setPushStarted(true);
    void startMediaPush(projectGroupId, packageName, selectedModules, dispatch, { mode, appId });
  };

  const handleClear = async () => {
    if (!clearConfirm) {
      setClearConfirm(true);
      return;
    }
    setClearing(true);
    setClearConfirm(false);
    const success =
      mode === "agent" && appId
        ? await clearAgentMedia(appId)
        : await clearAllMediaFromDevice(packageName);
    if (success) {
      setDeviceStatus(new Map());
    }
    setClearing(false);
  };

  const handleClose = () => {
    if (mediaPushJob) {
      dispatch(clearMediaPushJob());
    }
    onClose();
  };

  if (pushStarted) {
    return <MediaPushProgressModal onClose={handleClose} />;
  }

  return (
    <ModalShell
      title={
        <span className="flex items-center gap-2">
          {t("deviceInstaller.mediaManageTitle")}
          <Tooltip
            message={t("deviceInstaller.mediaPushInfoTooltip")}
            forceSingleLine={false}
            lineLength="w-64"
            classNameChildren="top-full mt-2 bottom-auto mb-0 -left-24"
          >
            <InfoCircleIcon className="w-4 h-4 text-gray-400 cursor-help" />
          </Tooltip>
        </span>
      }
      width="30rem"
      bodyHeight="340px"
      onClose={handleClose}
    >
      <div className="flex-1 flex flex-col min-h-0">
        {loading && (
          <div className="flex items-center gap-2 justify-center text-gray-500 flex-1">
            <Spinner className="w-5 h-5" color="green" />
            <span className="text-sm">{t("deviceInstaller.mediaPushFetchingManifest")}</span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 bg-red-50 rounded-lg p-3">
            <WarningCircleIcon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {!loading && !error && modules.length === 0 && (
          <p className="text-sm text-gray-500 text-center">
            {t("deviceInstaller.mediaPushNoModules")}
          </p>
        )}

        {!loading && !error && modules.length > 0 && (
          <div className="flex flex-col min-h-0">
            <p className="text-sm text-gray-600 mb-3 shrink-0">
              {t("deviceInstaller.mediaPushSelectModules")}
            </p>

            {modules.length > 1 && (
              <label className="flex items-center gap-2 px-3 py-2 mb-1 rounded-lg hover:bg-gray-50 cursor-pointer border-b border-gray-100 shrink-0">
                <input
                  type="checkbox"
                  checked={selectedIds.size === modules.length}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-green-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {t("deviceInstaller.mediaPushSelectAll")}
                </span>
              </label>
            )}

            <div className="overflow-y-auto min-h-0 flex-1">
              {modules.map((mod) => (
                <label
                  key={mod.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(mod.id)}
                    onChange={() => toggleModule(mod.id)}
                    className="w-4 h-4 accent-green-500"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-700 truncate block">{mod.name}</span>
                    {hasDeviceStatus &&
                      (() => {
                        const present = getModulePresentCount(mod);
                        const total = mod.medias.length;
                        if (present === total) {
                          return (
                            <span className="text-[11px] text-green-500 flex items-center gap-1">
                              <CheckIcon className="w-3 h-3" />
                              {t("deviceInstaller.mediaPushAllPresent")}
                            </span>
                          );
                        }
                        if (present > 0) {
                          return (
                            <span className="text-[11px] text-gray-400">
                              {t("deviceInstaller.mediaPushPartialPresent", {
                                present,
                                total,
                              })}
                            </span>
                          );
                        }
                        return null;
                      })()}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {t("deviceInstaller.mediaPushMediaCount", { count: mod.medias.length })}
                    {" · "}
                    {formatSize(getModuleSize(mod, mode))}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 mt-4">
        {!loading && !error && modules.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-gray-400">
                <div>
                  {t("deviceInstaller.mediaPushTotalFiles", { count: totalMediaCount })}
                  {totalSize > 0 && ` · ${formatSize(totalSize)}`}
                </div>
                {hasDeviceStatus && totalPresent > 0 && (
                  <div className="text-green-500">
                    {t("deviceInstaller.mediaPushAlreadyOnDevice", { count: totalPresent })}
                    {totalToTransfer > 0 &&
                      ` · ${t("deviceInstaller.mediaPushToTransfer", {
                        count: totalToTransfer,
                      })}`}
                  </div>
                )}
                {scanning && (
                  <div className="flex items-center gap-1 text-gray-400">
                    <Spinner className="w-3 h-3" color="green" />
                    {t("deviceInstaller.mediaPushScanningDevice")}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                {anyMediaOnDevice && !clearing && (
                  <button
                    onClick={handleClear}
                    className={`flex items-center justify-center gap-1.5 min-w-[9rem] px-3 py-1.5 text-xs rounded-lg transition-colors ${
                      clearConfirm
                        ? "text-white bg-red-500 hover:bg-red-600"
                        : "text-red-500 hover:text-red-600 hover:bg-red-50"
                    }`}
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                    {clearConfirm
                      ? t("deviceInstaller.mediaClearConfirm")
                      : t("deviceInstaller.mediaClearAll")}
                  </button>
                )}
                {clearing && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Spinner className="w-3 h-3" color="green" />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t("deviceInstaller.cancel")}
                </button>
                <button
                  onClick={handleStart}
                  disabled={selectedIds.size === 0}
                  className="px-4 py-2 text-sm text-white bg-green-500 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-40"
                >
                  {t("deviceInstaller.mediaPushStart")}
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-white bg-green-500 rounded-lg hover:opacity-90 transition-opacity"
            >
              {t("general.close")}
            </button>
          </div>
        )}

        {!error && (loading || modules.length === 0) && (
          <div className="flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {t("deviceInstaller.cancel")}
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default MediaPushModal;
